import type { Coupon } from "./coupon";
import { PROMOCODE_ADD_ERROR_CODES, type PromocodeAddError } from "./discount";
import { computed, signal, Signal, type ReadonlySignal } from "@preact/signals";

const MAX_COUPONS_COUNT = 2;

export type AddCouponResult = {
  isSuccess: boolean;
  error: PromocodeAddError | null;
};

class CouponManager {
  public readonly coupons: Signal<Readonly<Array<Coupon>>>;
  public canAddMoreCoupon: ReadonlySignal<boolean>;
  public noCoupons: ReadonlySignal<boolean>;

  constructor() {
    this.coupons = signal([]);
    this.canAddMoreCoupon = computed(
      () => this.coupons.value.length < MAX_COUPONS_COUNT,
    );
    this.noCoupons = computed(() => this.coupons.value.length === 0);
  }

  get hasExtraCoupon() {
    return this.coupons.peek().some((el) => el.isExtra);
  }

  // возвращает результат применения промокода
  addCoupon(coupon: Coupon): AddCouponResult {
    if (!this.canAddMoreCoupon.peek()) {
      return {
        isSuccess: false,
        error: PROMOCODE_ADD_ERROR_CODES.REACH_LIMIT,
      };
    }

    if (this.coupons.peek().find((c) => coupon.code === c.code)) {
      return {
        isSuccess: false,
        error: PROMOCODE_ADD_ERROR_CODES.ALREADY_APPLIED,
      };
    }

    if (
      this.coupons.peek().length === 0 ||
      coupon.isExtra !== this.hasExtraCoupon
    ) {
      this.coupons.value = this.coupons.value.concat(coupon);
      return {
        isSuccess: true,
        error: null,
      };
    }

    return {
      isSuccess: false,
      error: PROMOCODE_ADD_ERROR_CODES.NOT_WITH,
    };
  }

  removeCoupon(couponName: string) {
    const indexToRemove = this.coupons
      .peek()
      .findIndex((el) => el.code === couponName);
    if (indexToRemove !== -1) {
      this.coupons.value = this.coupons.value.toSpliced(indexToRemove, 1);
    }
  }
}

export const couponManager = new CouponManager();
